import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { marketplaceListingService } from '../services/api'; // Adjust path if needed
import { Link } from 'react-router-dom';

interface AdminListing {
    id: number;
    seller: {
        id: number;
        displayName: string;
        profilePicture?: string;
    };
    card?: {
        id: number;
        name: string;
        cardNumber: string;
        imageUrl?: string;
    };
    price: number;
    status: 'ACTIVE' | 'SOLD' | 'CANCELLED';
    listingType: 'WTS' | 'WTB';
    createdAt: string;
    isBundle: boolean;
    bundleName?: string;
    imageUrls?: string;
}

const AdminListingsPage: React.FC = () => {
    const { t } = useTranslation();
    const [listings, setListings] = useState<AdminListing[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedListing, setSelectedListing] = useState<AdminListing | null>(null);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    const fetchListings = async () => {
        setLoading(true);
        try {
            const response = await marketplaceListingService.getAdminListings({
                page,
                limit: 20,
                status: statusFilter === 'all' ? undefined : statusFilter,
                sortBy: 'createdAt',
                sortOrder: 'DESC'
            });
            if (response.data.success) {
                setListings(response.data.data);
                setTotalPages(response.data.pagination.totalPages);
            }
        } catch (error) {
            console.error('Error fetching listings:', error);
            setMessage({ type: 'error', text: 'Failed to load listings' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchListings();
    }, [page, statusFilter]);

    const handleCancelListing = async () => {
        if (!selectedListing) return;

        setProcessing(true);
        setMessage(null);
        try {
            await marketplaceListingService.cancelListing(selectedListing.id.toString(), cancelReason);
            setMessage({ type: 'success', text: t('adminListings.messages.cancelSuccess') });
            setShowCancelModal(false);
            setCancelReason('');
            fetchListings(); // Refresh list
        } catch (error) {
            console.error('Error cancelling listing:', error);
            setMessage({ type: 'error', text: t('adminListings.messages.cancelError') });
        } finally {
            setProcessing(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'ACTIVE': return 'bg-green-100 text-green-800';
            case 'SOLD': return 'bg-blue-100 text-blue-800';
            case 'CANCELLED': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const openCancelModal = (listing: AdminListing) => {
        setSelectedListing(listing);
        setCancelReason('');
        setShowCancelModal(true);
        setMessage(null);
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">{t('adminListings.title')}</h1>
                    <p className="text-gray-600 mt-1">{t('adminListings.subtitle')}</p>
                </div>
            </div>

            {message && (
                <div className={`mb-6 p-4 rounded-lg flex items-center ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    <span className="mr-2 text-xl">{message.type === 'success' ? '✓' : '⚠'}</span>
                    {message.text}
                </div>
            )}

            {/* Filters */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6 flex items-center gap-4">
                <select
                    value={statusFilter}
                    onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPage(1);
                    }}
                    className="border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                >
                    <option value="all">{t('adminListings.filters.all')}</option>
                    <option value="ACTIVE">{t('adminListings.filters.active')}</option>
                    <option value="SOLD">{t('adminListings.filters.sold')}</option>
                    <option value="CANCELLED">{t('adminListings.filters.cancelled')}</option>
                </select>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('adminListings.table.image')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('adminListings.table.cardName')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('adminListings.table.price')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('adminListings.table.seller')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('adminListings.table.status')}
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('adminListings.table.date')}
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {t('adminListings.table.actions')}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                                        Loading...
                                    </td>
                                </tr>
                            ) : listings.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                                        No listings found
                                    </td>
                                </tr>
                            ) : (
                                listings.map((listing) => (
                                    <tr key={listing.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="h-12 w-12 rounded bg-gray-100 overflow-hidden">
                                                {listing.imageUrls ? (
                                                    <img
                                                        src={JSON.parse(listing.imageUrls)[0]}
                                                        alt="Listing"
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : listing.card?.imageUrl ? (
                                                    <img
                                                        src={listing.card.imageUrl}
                                                        alt={listing.card.name}
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="h-full w-full flex items-center justify-center text-gray-400 text-xs">No Img</div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-medium text-gray-900">
                                                {listing.isBundle ? listing.bundleName || 'Bundle' : listing.card?.name || 'Unknown Card'}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {listing.listingType === 'WTB' ? (
                                                    <span className="text-orange-600 font-medium">WTB</span>
                                                ) : (
                                                    <span className="text-blue-600 font-medium">WTS</span>
                                                )}
                                                {listing.card?.cardNumber && ` • ${listing.card.cardNumber}`}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            ฿{Number(listing.price).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="h-8 w-8 rounded-full bg-gray-200 overflow-hidden mr-2">
                                                    {listing.seller.profilePicture ? (
                                                        <img src={listing.seller.profilePicture} alt="" className="h-full w-full object-cover" />
                                                    ) : (
                                                        <div className="h-full w-full flex items-center justify-center text-gray-500 text-xs">
                                                            {listing.seller.displayName.substring(0, 1)}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-sm text-gray-900">{listing.seller.displayName}</div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(listing.status)}`}>
                                                {t(`adminListings.filters.${listing.status.toLowerCase()}`, listing.status)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(listing.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <Link to={`/marketplace/listing/${listing.id}`} className="text-indigo-600 hover:text-indigo-900 mr-4">
                                                {t('adminListings.actions.view')}
                                            </Link>
                                            {listing.status === 'ACTIVE' && (
                                                <button
                                                    onClick={() => openCancelModal(listing)}
                                                    className="text-red-600 hover:text-red-900"
                                                >
                                                    {t('adminListings.actions.cancel')}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Page <span className="font-medium">{page}</span> of <span className="font-medium">{totalPages}</span>
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                    <button
                                        onClick={() => setPage(p => Math.max(1, p - 1))}
                                        disabled={page === 1}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                        disabled={page === totalPages}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Cancel Modal */}
            {showCancelModal && selectedListing && (
                <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowCancelModal(false)}></div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                        <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                            {t('adminListings.cancelModal.title')}
                                        </h3>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                {t('adminListings.cancelModal.message')}
                                            </p>
                                            <div className="bg-gray-50 p-3 rounded mt-3 border border-gray-200">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {selectedListing.isBundle ? selectedListing.bundleName : selectedListing.card?.name}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    Seller: {selectedListing.seller.displayName} | Price: ฿{selectedListing.price.toLocaleString()}
                                                </div>
                                            </div>
                                            <div className="mt-4">
                                                <label className="block text-sm font-medium text-gray-700">
                                                    {t('adminListings.cancelModal.reasonLabel')}
                                                </label>
                                                <textarea
                                                    rows={3}
                                                    value={cancelReason}
                                                    onChange={(e) => setCancelReason(e.target.value)}
                                                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    onClick={handleCancelListing}
                                    disabled={processing}
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                                >
                                    {processing ? 'Processing...' : t('adminListings.cancelModal.confirm')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowCancelModal(false)}
                                    disabled={processing}
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                >
                                    {t('adminListings.cancelModal.cancel')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminListingsPage;
